/**
 * PatchingSchedule.js
 * Three tabs:
 *  1. Patch Rings     — Test → Pilot → Production ring assignment per device
 *  2. Maintenance     — Business hours, blackouts, holidays, reboot policy
 *  3. Release Matrix  — Microsoft Patch Tuesday 4-phase rollout calendar
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

// ── Helpers ────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
}
function timeSince(d) {
  if (!d) return 'never';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

const RING_COLOR = {
  test:       { bg: '#eff4ff', color: '#1a56db', border: '#bfdbfe', icon: '🧪' },
  pilot:      { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', icon: '🚀' },
  production: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', icon: '✅' },
};
const RING_DESC = {
  test:       'First to receive patches. Small group — typically IT team / lab machines.',
  pilot:      '~10-20% of devices. Early adopters, business units with high IT literacy.',
  production: 'Remaining devices. Receive patches after Test & Pilot rings validate.',
};

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const WIN_TYPE_COLOR = {
  maintenance: { bg: '#eff4ff', color: '#1a56db' },
  blackout:    { bg: '#fee2e2', color: '#dc2626' },
  holiday:     { bg: '#fef3c7', color: '#d97706' },
};

// ── Patch Release Matrix (existing) ───────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MATRIX = {
  patchTuesday: ['Tue 1-13','Tue 2-10','Tue 3-10','Tue 4-14','Tue 5-12','Tue 6-9','Tue 7-14','Tue 8-11','Tue 9-8','Tue 10-13','Tue 11-10','Tue 12-8'],
  phases: [
    { key:'phase1', label:'Phase 1 · Test',       desc:'Test group (~5% devices)',    color:'#1565c0', dates:['Sun 1-18','Sun 2-15','Sun 3-15','Sun 4-19','Sun 5-17','Sun 6-14','Sun 7-19','Sun 8-16','Sun 9-13','Sun 10-18','Sun 11-15','Sun 12-13'] },
    { key:'phase2', label:'Phase 2 · Pilot',      desc:'Pilot group (~20% devices)',  color:'#b45309', dates:['Sun 1-25','Sun 2-22','Sun 3-22','Sun 4-26','Sun 5-24','Sun 6-21','Sun 7-26','Sun 8-23','Sun 9-20','Sun 10-25','Sun 11-22','Sun 12-20'] },
    { key:'phase3', label:'Phase 3 · Prod 50%',   desc:'Production wave 1 (~50%)',    color:'#1a6b3c', dates:['Sun 2-1','Sun 3-1','Sun 3-29','Sun 5-3','Sun 5-31','Sun 6-28','Sun 8-2','Sun 8-30','Sun 9-27','Sun 11-1','Sun 11-29','Sun 12-27'] },
    { key:'phase4', label:'Phase 4 · Prod 100%',  desc:'Production full rollout',     color:'#7c3aed', dates:['Sun 2-8','Sun 3-8','Sun 4-5','Sun 5-10','Sun 6-7','Sun 7-5','Sun 8-9','Sun 9-6','Sun 10-4','Sun 11-8','Sun 12-6','Sun 1-3'] },
  ]
};

function parseDay(s)  { return parseInt((s||'').split(' ')[1]?.split('-')[1]); }
function parseMon(s)  { return parseInt((s||'').split(' ')[1]?.split('-')[0]); }
function getDateStatus(s, col) {
  const d = parseDay(s), m = parseMon(s);
  if (!d || !m) return 'future';
  const y = new Date().getFullYear();
  const yr = col === 11 && m === 1 ? y+1 : y;
  const diff = Math.floor((new Date(yr,m-1,d) - new Date(new Date().setHours(0,0,0,0))) / 86400000);
  if (diff < 0) return 'past';
  if (diff === 0) return 'today';
  if (diff <= 7) return 'soon';
  return 'future';
}

// ══════════════════════════════════════════════════════════════════════════
export default function PatchingSchedule({ user }) {
  const [tab, setTab] = useState('rings');
  const isAdmin = user?.role === 'admin';

  // ── Ring state ────────────────────────────────────────────────────────
  const [ringData,     setRingData]     = useState(null);
  const [ringsLoading, setRingsLoading] = useState(true);
  const [ringFilter,   setRingFilter]   = useState('all');
  const [selectedDevs, setSelectedDevs] = useState(new Set());
  const [assigning,    setAssigning]    = useState(false);
  const [bulkRing,     setBulkRing]     = useState('pilot');
  const [ringMsg,      setRingMsg]      = useState('');

  // ── Maintenance state ─────────────────────────────────────────────────
  const [windows,     setWindows]     = useState([]);
  const [winLoading,  setWinLoading]  = useState(true);
  const [showWinForm, setShowWinForm] = useState(false);
  const [editWin,     setEditWin]     = useState(null);
  const [currentWin,  setCurrentWin]  = useState(null);
  const [winMsg,      setWinMsg]      = useState('');
  const [winForm, setWinForm] = useState({
    name: '', description: '', type: 'maintenance',
    ring: 'all', deviceType: 'all',
    isRecurring: true, dayOfWeek: 6, startTime: '22:00', endTime: '02:00',
    startDate: '', endDate: '',
    allowReboot: true, forceReboot: false, rebootDelaySec: 300,
    active: true, notes: ''
  });

  // ── Load rings ─────────────────────────────────────────────────────────
  const loadRings = useCallback(async () => {
    setRingsLoading(true);
    try {
      const r = await api.getRings();
      setRingData(r.data);
    } catch (e) { console.error('Rings:', e.message); }
    finally { setRingsLoading(false); }
  }, []);

  // ── Load windows ───────────────────────────────────────────────────────
  const loadWindows = useCallback(async () => {
    setWinLoading(true);
    try {
      const [wRes, cRes] = await Promise.allSettled([
        api.getMaintenanceWindows(),
        api.getCurrentWindowStatus()
      ]);
      if (wRes.status === 'fulfilled') setWindows(wRes.value.data);
      if (cRes.status === 'fulfilled') setCurrentWin(cRes.value.data);
    } catch (e) { console.error('Windows:', e.message); }
    finally { setWinLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'rings')       loadRings();
    if (tab === 'maintenance') loadWindows();
  }, [tab, loadRings, loadWindows]);

  // ── Ring actions ───────────────────────────────────────────────────────
  async function assignSingle(deviceId, ring) {
    try {
      await api.assignRing(deviceId, ring);
      setRingMsg(`✔ Device moved to ${ring} ring`);
      setTimeout(() => setRingMsg(''), 3000);
      loadRings();
    } catch (e) { setRingMsg('Error: ' + e.message); }
  }

  async function bulkAssign() {
    if (selectedDevs.size === 0) return;
    setAssigning(true);
    try {
      await api.bulkAssignRing([...selectedDevs], bulkRing);
      setRingMsg(`✔ ${selectedDevs.size} devices moved to ${bulkRing} ring`);
      setTimeout(() => setRingMsg(''), 4000);
      setSelectedDevs(new Set());
      loadRings();
    } catch (e) { setRingMsg('Error: ' + e.message); }
    finally { setAssigning(false); }
  }

  function toggleDev(id) {
    setSelectedDevs(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  // ── Window actions ────────────────────────────────────────────────────
  function openWinForm(win = null) {
    if (win) {
      setWinForm({ ...win, isRecurring: win.isRecurring ?? true });
      setEditWin(win.id);
    } else {
      setWinForm({ name:'', description:'', type:'maintenance', ring:'all', deviceType:'all',
        isRecurring:true, dayOfWeek:6, startTime:'22:00', endTime:'02:00',
        startDate:'', endDate:'', allowReboot:true, forceReboot:false,
        rebootDelaySec:300, active:true, notes:'' });
      setEditWin(null);
    }
    setShowWinForm(true);
  }

  async function saveWindow() {
    try {
      if (editWin) {
        await api.updateMaintenanceWindow(editWin, winForm);
        setWinMsg('✔ Window updated');
      } else {
        await api.createMaintenanceWindow(winForm);
        setWinMsg('✔ Window created');
      }
      setTimeout(() => setWinMsg(''), 3000);
      setShowWinForm(false);
      loadWindows();
    } catch (e) { setWinMsg('Error: ' + e.message); }
  }

  async function deleteWindow(id) {
    if (!window.confirm('Delete this maintenance window?')) return;
    try {
      await api.deleteMaintenanceWindow(id);
      setWinMsg('✔ Deleted');
      setTimeout(() => setWinMsg(''), 3000);
      loadWindows();
    } catch (e) { setWinMsg('Error: ' + e.message); }
  }

  // All devices flat list
  const allDevices = ringData?.devices || [];
  const visibleDevices = ringFilter === 'all' ? allDevices
    : allDevices.filter(d => (d.patchRing || 'production') === ringFilter);

  const currentMonth = new Date().getMonth();

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">📅 Patch Schedule &amp; Rings</div>
          <div className="page-subtitle">
            Manage deployment rings · Maintenance windows · Patch Tuesday calendar
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'2px solid #e8ecf4', marginBottom:24 }}>
        {[
          { id:'rings',       label:'🔗 Patch Rings' },
          { id:'maintenance', label:'🛠 Maintenance Schedule' },
          { id:'matrix',      label:'📆 Release Matrix' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background:'none', border:'none', cursor:'pointer',
            padding:'10px 20px', fontSize:13, fontWeight:600,
            color: tab===t.id ? '#1a56db' : '#94a3b8',
            borderBottom: tab===t.id ? '2px solid #1a56db' : '2px solid transparent',
            marginBottom:-2
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          TAB 1: PATCH RINGS
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'rings' && (
        <>
          {/* Ring overview cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:20 }}>
            {['test','pilot','production'].map(ring => {
              const rc = RING_COLOR[ring];
              const count = ringData?.summary?.[ring] || 0;
              const total = ringData?.summary?.total || 1;
              return (
                <div key={ring} style={{ background:rc.bg, border:`1px solid ${rc.border}`,
                  borderRadius:12, padding:'18px 20px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                    <span style={{ fontSize:24 }}>{rc.icon}</span>
                    <div>
                      <div style={{ fontSize:15, fontWeight:700, color:rc.color, textTransform:'capitalize' }}>
                        {ring} Ring
                      </div>
                      <div style={{ fontSize:11, color:'#64748b' }}>{RING_DESC[ring]}</div>
                    </div>
                  </div>
                  <div style={{ fontSize:28, fontWeight:800, color:rc.color }}>{count}</div>
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                    {total > 0 ? Math.round(count/total*100) : 0}% of total devices
                  </div>
                  <div style={{ background:'#e8ecf4', borderRadius:4, height:4, marginTop:8, overflow:'hidden' }}>
                    <div style={{ width:`${total>0?count/total*100:0}%`, height:'100%',
                      background:rc.color, borderRadius:4 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Ring info banner */}
          <div className="alert info" style={{ marginBottom:20 }}>
            🔗 <strong>Deployment rings</strong> control which devices receive patches first.
            Patch Released → <strong>Test</strong> (validate) → <strong>Pilot</strong> (broad testing) → <strong>Production</strong> (full rollout).
            Assign devices to rings using the table below. Use bulk-assign to move multiple devices at once.
          </div>

          {ringMsg && (
            <div className={`alert ${ringMsg.startsWith('Error') ? 'error' : 'success'}`}
              style={{ marginBottom:12 }}>{ringMsg}</div>
          )}

          {/* Bulk assign bar */}
          {isAdmin && selectedDevs.size > 0 && (
            <div style={{ background:'#0f2350', color:'#fff', borderRadius:10,
              padding:'12px 20px', marginBottom:16, display:'flex', alignItems:'center', gap:16 }}>
              <span style={{ fontSize:13, fontWeight:600 }}>
                {selectedDevs.size} device{selectedDevs.size>1?'s':''} selected
              </span>
              <select value={bulkRing} onChange={e => setBulkRing(e.target.value)}
                style={{ padding:'6px 10px', borderRadius:6, border:'none', fontSize:13 }}>
                <option value="test">→ Test Ring 🧪</option>
                <option value="pilot">→ Pilot Ring 🚀</option>
                <option value="production">→ Production Ring ✅</option>
              </select>
              <button className="btn btn-sm" onClick={bulkAssign} disabled={assigning}
                style={{ background:'#1a56db', color:'#fff', border:'none', fontWeight:700 }}>
                {assigning ? '⟳ Moving...' : 'Move to Ring'}
              </button>
              <button className="btn btn-sm btn-ghost" style={{ color:'#fff' }}
                onClick={() => setSelectedDevs(new Set())}>Clear</button>
            </div>
          )}

          {/* Device table */}
          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                <div className="panel-title-icon">🖥️</div>
                Device Ring Assignments
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {['all','test','pilot','production'].map(r => (
                  <button key={r} className={`btn btn-sm ${ringFilter===r?'btn-primary':'btn-outline'}`}
                    onClick={() => setRingFilter(r)}>
                    {r === 'all' ? 'All' : r.charAt(0).toUpperCase()+r.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {ringsLoading ? <div className="loading">Loading...</div> : (
              <table>
                <thead>
                  <tr>
                    {isAdmin && <th style={{ width:36 }}></th>}
                    <th>Hostname</th>
                    <th>OS</th>
                    <th>Device Type</th>
                    <th>Status</th>
                    <th>Last Seen</th>
                    <th>Current Ring</th>
                    {isAdmin && <th>Change Ring</th>}
                  </tr>
                </thead>
                <tbody>
                  {visibleDevices.length === 0 && (
                    <tr><td colSpan={isAdmin?8:6} className="empty">No devices found.</td></tr>
                  )}
                  {visibleDevices.map(d => {
                    const ring = d.patchRing || 'production';
                    const rc = RING_COLOR[ring];
                    const isSel = selectedDevs.has(d.id);
                    return (
                      <tr key={d.id} style={{ background: isSel ? '#eff4ff' : undefined }}>
                        {isAdmin && (
                          <td>
                            <input type="checkbox" checked={isSel}
                              onChange={() => toggleDev(d.id)} style={{ cursor:'pointer' }} />
                          </td>
                        )}
                        <td><strong style={{ fontSize:13 }}>{d.hostname}</strong></td>
                        <td>
                          <div style={{ fontSize:13 }}>{d.os}</div>
                          <div style={{ fontSize:11, color:'#94a3b8' }}>{d.osVersion || '—'}</div>
                        </td>
                        <td>
                          <span style={{ fontSize:12, color:'#64748b' }}>
                            {d.deviceType === 'server' ? '🖥 Server' : d.deviceType === 'workstation' ? '💻 Workstation' : '⚙ Other'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${d.status==='online'?'online':'offline'}`}>
                            ● {d.status}
                          </span>
                        </td>
                        <td style={{ fontSize:12, color:'#94a3b8' }}>{timeSince(d.lastHeartbeat || d.updatedAt)}</td>
                        <td>
                          <span style={{ fontSize:12, fontWeight:700, background:rc.bg,
                            color:rc.color, padding:'3px 10px', borderRadius:12,
                            border:`1px solid ${rc.border}` }}>
                            {rc.icon} {ring.charAt(0).toUpperCase()+ring.slice(1)}
                          </span>
                        </td>
                        {isAdmin && (
                          <td>
                            <select value={ring}
                              onChange={e => assignSingle(d.id, e.target.value)}
                              style={{ padding:'4px 8px', borderRadius:6,
                                border:'1px solid #e8ecf4', fontSize:12, cursor:'pointer' }}>
                              <option value="test">🧪 Test</option>
                              <option value="pilot">🚀 Pilot</option>
                              <option value="production">✅ Production</option>
                            </select>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 2: MAINTENANCE SCHEDULE
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'maintenance' && (
        <>
          {/* Current status banner */}
          {currentWin && (
            <div style={{
              background: currentWin.inMaintenanceWindow ? '#f0fdf4' : '#f8fafc',
              border: `1px solid ${currentWin.inMaintenanceWindow ? '#bbf7d0' : '#e8ecf4'}`,
              borderRadius:10, padding:'12px 20px', marginBottom:20,
              display:'flex', alignItems:'center', gap:12
            }}>
              <span style={{ fontSize:22 }}>{currentWin.inMaintenanceWindow ? '🟢' : '⚪'}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:700,
                  color: currentWin.inMaintenanceWindow ? '#16a34a' : '#64748b' }}>
                  {currentWin.inMaintenanceWindow
                    ? `In Maintenance Window — ${currentWin.activeWindows.map(w=>w.name).join(', ')}`
                    : 'No Active Maintenance Window Right Now'}
                </div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>
                  Current time: {currentWin.currentTime} · {DAY_NAMES[currentWin.currentDay]} · {currentWin.currentDate}
                </div>
              </div>
              {currentWin.inMaintenanceWindow && (
                <span style={{ marginLeft:'auto', fontSize:11, fontWeight:700,
                  background:'#dcfce7', color:'#16a34a', padding:'4px 12px', borderRadius:20 }}>
                  ✔ Patching Allowed
                </span>
              )}
            </div>
          )}

          {winMsg && (
            <div className={`alert ${winMsg.startsWith('Error') ? 'error' : 'success'}`}
              style={{ marginBottom:12 }}>{winMsg}</div>
          )}

          {/* Header action */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <div style={{ fontSize:14, fontWeight:600, color:'#1e2a3b' }}>Maintenance Windows</div>
              <div style={{ fontSize:12, color:'#94a3b8' }}>
                Define when patches can be deployed, reboots allowed, and blackout periods.
              </div>
            </div>
            {isAdmin && (
              <button className="btn btn-primary" onClick={() => openWinForm()}>
                + Add Window
              </button>
            )}
          </div>

          {/* Default templates info */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
            {[
              { icon:'🖥️', title:'Servers', example:'Saturday 22:00 – 02:00', ring:'production', deviceType:'server' },
              { icon:'💻', title:'Workstations', example:'Friday 20:00 – 06:00', ring:'production', deviceType:'workstation' },
              { icon:'🚫', title:'Blackout Example', example:'Dec 24 – Jan 2 (Holiday)', ring:'all', deviceType:'all' },
            ].map((t,i) => (
              <div key={i} style={{ background:'#f8fafc', border:'1px solid #e8ecf4',
                borderRadius:10, padding:'14px 16px', display:'flex', alignItems:'flex-start', gap:10 }}>
                <span style={{ fontSize:22 }}>{t.icon}</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#1e2a3b' }}>{t.title}</div>
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{t.example}</div>
                  {isAdmin && (
                    <button className="btn btn-sm btn-outline" style={{ marginTop:8, fontSize:11 }}
                      onClick={() => openWinForm({
                        name: `${t.title} Maintenance`, type: t.icon==='🚫'?'blackout':'maintenance',
                        ring: t.ring, deviceType: t.deviceType,
                        isRecurring: t.icon!=='🚫', dayOfWeek: t.icon==='🖥️'?6:5,
                        startTime: t.icon==='🖥️'?'22:00':'20:00',
                        endTime: t.icon==='🖥️'?'02:00':'06:00',
                        allowReboot:true, forceReboot:false, rebootDelaySec:300,
                        active:true
                      })}>
                      Use Template
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Windows list */}
          {winLoading ? <div className="loading">Loading...</div> : (
            <div className="panel">
              {windows.length === 0 ? (
                <div className="empty" style={{ padding:40 }}>
                  No maintenance windows configured yet.
                  {isAdmin && <> Click <strong>+ Add Window</strong> to create one.</>}
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                  {windows.map((w, i) => {
                    const tc = WIN_TYPE_COLOR[w.type] || WIN_TYPE_COLOR.maintenance;
                    return (
                      <div key={w.id} style={{
                        padding:'14px 20px', borderBottom:'1px solid #f0f4ff',
                        display:'flex', alignItems:'flex-start', gap:14,
                        background: w.active ? '#fff' : '#f8fafc',
                        opacity: w.active ? 1 : 0.6
                      }}>
                        <div style={{ width:40, height:40, borderRadius:8, background:tc.bg,
                          display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                          {w.type==='blackout'?'🚫':w.type==='holiday'?'🎉':'🛠'}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                            <span style={{ fontSize:14, fontWeight:700, color:'#1e2a3b' }}>{w.name}</span>
                            <span style={{ fontSize:10, fontWeight:700, background:tc.bg,
                              color:tc.color, padding:'2px 8px', borderRadius:10 }}>
                              {w.type.toUpperCase()}
                            </span>
                            {!w.active && (
                              <span style={{ fontSize:10, color:'#94a3b8', background:'#f1f5f9',
                                padding:'2px 8px', borderRadius:10 }}>INACTIVE</span>
                            )}
                          </div>
                          <div style={{ fontSize:12, color:'#64748b', marginTop:4, display:'flex', gap:16, flexWrap:'wrap' }}>
                            {w.isRecurring ? (
                              <span>📅 Every {DAY_NAMES[w.dayOfWeek]} {w.startTime}–{w.endTime}</span>
                            ) : (
                              <span>📅 {w.startDate}{w.endDate && w.endDate !== w.startDate ? ` → ${w.endDate}` : ''}</span>
                            )}
                            <span>Ring: <strong>{w.ring}</strong></span>
                            <span>Devices: <strong>{w.deviceType}</strong></span>
                            {w.forceReboot && <span style={{ color:'#dc2626' }}>⚡ Force Reboot ({w.rebootDelaySec}s notice)</span>}
                            {w.allowReboot && !w.forceReboot && <span style={{ color:'#16a34a' }}>↻ Reboot allowed</span>}
                            {!w.allowReboot && <span style={{ color:'#d97706' }}>🚫 No reboots</span>}
                          </div>
                          {w.notes && (
                            <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>📝 {w.notes}</div>
                          )}
                        </div>
                        {isAdmin && (
                          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                            <button className="btn btn-sm btn-outline" onClick={() => openWinForm(w)}>Edit</button>
                            <button className="btn btn-sm" onClick={() => deleteWindow(w.id)}
                              style={{ background:'#fee2e2', color:'#dc2626', border:'none' }}>Delete</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Window Form Modal ── */}
          {showWinForm && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
              display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
              <div style={{ background:'#fff', borderRadius:12, padding:28, width:560,
                maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize:16, fontWeight:700, color:'#1e2a3b', marginBottom:20 }}>
                  {editWin ? 'Edit Maintenance Window' : 'New Maintenance Window'}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {/* Name */}
                  <div style={{ gridColumn:'1/-1' }}>
                    <label className="form-label">Name *</label>
                    <input className="form-input" value={winForm.name}
                      onChange={e => setWinForm(f=>({...f, name:e.target.value}))}
                      placeholder="e.g. Servers Weekend Maintenance" />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="form-label">Type</label>
                    <select className="form-input" value={winForm.type}
                      onChange={e => setWinForm(f=>({...f, type:e.target.value}))}>
                      <option value="maintenance">🛠 Maintenance</option>
                      <option value="blackout">🚫 Blackout</option>
                      <option value="holiday">🎉 Holiday</option>
                    </select>
                  </div>

                  {/* Ring */}
                  <div>
                    <label className="form-label">Applies to Ring</label>
                    <select className="form-input" value={winForm.ring}
                      onChange={e => setWinForm(f=>({...f, ring:e.target.value}))}>
                      <option value="all">All Rings</option>
                      <option value="test">Test Ring</option>
                      <option value="pilot">Pilot Ring</option>
                      <option value="production">Production Ring</option>
                    </select>
                  </div>

                  {/* Device Type */}
                  <div>
                    <label className="form-label">Device Type</label>
                    <select className="form-input" value={winForm.deviceType}
                      onChange={e => setWinForm(f=>({...f, deviceType:e.target.value}))}>
                      <option value="all">All Devices</option>
                      <option value="server">Servers Only</option>
                      <option value="workstation">Workstations Only</option>
                    </select>
                  </div>

                  {/* Recurring toggle */}
                  <div>
                    <label className="form-label">Schedule Type</label>
                    <select className="form-input" value={winForm.isRecurring?'recurring':'onetime'}
                      onChange={e => setWinForm(f=>({...f, isRecurring:e.target.value==='recurring'}))}>
                      <option value="recurring">Recurring (weekly)</option>
                      <option value="onetime">One-time / Date range</option>
                    </select>
                  </div>

                  {winForm.isRecurring ? (
                    <>
                      <div>
                        <label className="form-label">Day of Week</label>
                        <select className="form-input" value={winForm.dayOfWeek}
                          onChange={e => setWinForm(f=>({...f, dayOfWeek:parseInt(e.target.value)}))}>
                          {DAY_NAMES.map((d,i) => <option key={i} value={i}>{d}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Start Time (24h)</label>
                        <input className="form-input" type="time" value={winForm.startTime}
                          onChange={e => setWinForm(f=>({...f, startTime:e.target.value}))} />
                      </div>
                      <div>
                        <label className="form-label">End Time (24h)</label>
                        <input className="form-input" type="time" value={winForm.endTime}
                          onChange={e => setWinForm(f=>({...f, endTime:e.target.value}))} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="form-label">Start Date</label>
                        <input className="form-input" type="date" value={winForm.startDate}
                          onChange={e => setWinForm(f=>({...f, startDate:e.target.value}))} />
                      </div>
                      <div>
                        <label className="form-label">End Date</label>
                        <input className="form-input" type="date" value={winForm.endDate}
                          onChange={e => setWinForm(f=>({...f, endDate:e.target.value}))} />
                      </div>
                    </>
                  )}

                  {/* Reboot policy */}
                  <div style={{ gridColumn:'1/-1', background:'#f8fafc', borderRadius:8,
                    padding:'12px 14px', border:'1px solid #e8ecf4' }}>
                    <div style={{ fontSize:12, fontWeight:700, color:'#1e2a3b', marginBottom:10 }}>
                      Reboot Policy
                    </div>
                    <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                        <input type="checkbox" checked={winForm.allowReboot}
                          onChange={e => setWinForm(f=>({...f, allowReboot:e.target.checked}))} />
                        <span style={{ fontSize:12 }}>Allow reboots</span>
                      </label>
                      <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                        <input type="checkbox" checked={winForm.forceReboot}
                          onChange={e => setWinForm(f=>({...f, forceReboot:e.target.checked}))}
                          disabled={!winForm.allowReboot} />
                        <span style={{ fontSize:12, color: winForm.allowReboot?'inherit':'#94a3b8' }}>
                          Force reboot after patch
                        </span>
                      </label>
                      {winForm.forceReboot && (
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontSize:12 }}>Notice:</span>
                          <input className="form-input" type="number" style={{ width:80 }}
                            value={winForm.rebootDelaySec}
                            onChange={e => setWinForm(f=>({...f, rebootDelaySec:parseInt(e.target.value)||300}))} />
                          <span style={{ fontSize:12, color:'#94a3b8' }}>seconds</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  <div style={{ gridColumn:'1/-1' }}>
                    <label className="form-label">Notes (optional)</label>
                    <textarea className="form-input" rows={2} value={winForm.notes}
                      onChange={e => setWinForm(f=>({...f, notes:e.target.value}))}
                      placeholder="e.g. Approved by Change Board CAB-2026-001" />
                  </div>

                  {/* Active */}
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                      <input type="checkbox" checked={winForm.active}
                        onChange={e => setWinForm(f=>({...f, active:e.target.checked}))} />
                      <span style={{ fontSize:13, fontWeight:500 }}>Active (enable this window)</span>
                    </label>
                  </div>
                </div>

                <div className="flex-gap" style={{ justifyContent:'flex-end', marginTop:20 }}>
                  <button className="btn btn-ghost" onClick={() => setShowWinForm(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={saveWindow}
                    disabled={!winForm.name}>
                    {editWin ? 'Save Changes' : 'Create Window'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 3: RELEASE MATRIX
      ══════════════════════════════════════════════════════════════ */}
      {tab === 'matrix' && (
        <div>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:600, color:'#1e2a3b' }}>Microsoft Patch Tuesday Release Matrix</div>
            <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>
              4-phase rollout aligned to Test → Pilot → Production rings ·
              Current month: <strong style={{ color:'#1a56db' }}>{MONTHS[currentMonth]} {new Date().getFullYear()}</strong>
            </div>
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:900 }}>
              <thead>
                <tr>
                  <th style={{ background:'#f8fafc', padding:'10px 14px', textAlign:'left',
                    border:'1px solid #e8ecf4', minWidth:180, fontSize:13, fontWeight:700, color:'#1e2a3b' }}>
                    Phase
                  </th>
                  <th style={{ background:'#f8fafc', padding:'10px 14px', textAlign:'left',
                    border:'1px solid #e8ecf4', minWidth:200, fontSize:12, color:'#64748b' }}>
                    Description
                  </th>
                  {MONTHS.map((m,i) => (
                    <th key={m} style={{
                      background: i===currentMonth ? '#eff4ff' : '#f8fafc',
                      color: i===currentMonth ? '#1a56db' : '#94a3b8',
                      padding:'8px 4px', border:'1px solid #e8ecf4',
                      textAlign:'center', minWidth:68, fontSize:12,
                      fontWeight: i===currentMonth ? 700 : 400
                    }}>
                      {m}{i===currentMonth && <div style={{ fontSize:9, color:'#1a56db' }}>▼NOW</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Patch Tuesday row */}
                <tr>
                  <td style={{ background:'#eff4ff', color:'#1a56db', padding:'10px 14px',
                    border:'1px solid #e8ecf4', fontWeight:700 }}>
                    🗓️ Patch Tuesday
                  </td>
                  <td style={{ background:'#eff4ff', color:'#64748b', padding:'10px 14px',
                    border:'1px solid #e8ecf4', fontSize:11 }}>Microsoft monthly security release</td>
                  {MATRIX.patchTuesday.map((d,i) => {
                    const st = getDateStatus(d,i);
                    const cur = i===currentMonth;
                    return (
                      <td key={i} style={{ padding:'8px 4px', border:'1px solid #e8ecf4',
                        textAlign:'center', fontSize:11,
                        background: st==='today'?'#dcfce7': st==='soon'?'#fef3c7': cur?'#eff4ff':'#f8fafc',
                        color: st==='today'?'#16a34a': st==='soon'?'#d97706': cur?'#1a56db':'#94a3b8',
                        fontWeight: (st==='today'||cur) ? 700 : 400 }}>
                        {d.replace(/^Tue /,'')}
                        {st==='today' && <div style={{ fontSize:9, color:'#16a34a' }}>TODAY</div>}
                        {st==='soon'  && <div style={{ fontSize:9, color:'#d97706' }}>SOON</div>}
                      </td>
                    );
                  })}
                </tr>
                {/* Phase rows */}
                {MATRIX.phases.map(phase => (
                  <tr key={phase.key}>
                    <td style={{ background:phase.color+'22', color:phase.color,
                      padding:'10px 14px', border:'1px solid #e8ecf4', fontWeight:700 }}>
                      {phase.label}
                    </td>
                    <td style={{ background:phase.color+'11', color:'#64748b',
                      padding:'10px 14px', border:'1px solid #e8ecf4', fontSize:11 }}>
                      {phase.desc}
                    </td>
                    {phase.dates.map((d,i) => {
                      const st = getDateStatus(d,i);
                      const cur = i===currentMonth;
                      return (
                        <td key={i} style={{ padding:'8px 4px', border:'1px solid #e8ecf4',
                          textAlign:'center', fontSize:11,
                          background: st==='today'?'#dcfce7': st==='soon'?'#fef3c7': cur?phase.color+'22':'#f8fafc',
                          color: st==='today'?'#16a34a': st==='soon'?'#d97706': cur?phase.color:'#94a3b8',
                          fontWeight: (st==='today'||cur) ? 700 : 400 }}>
                          {d.replace(/^Sun /,'')}
                          {st==='today' && <div style={{ fontSize:9, color:'#16a34a' }}>TODAY</div>}
                          {st==='soon'  && <div style={{ fontSize:9, color:'#d97706' }}>SOON</div>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Ring → Phase mapping */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginTop:20 }}>
            {[
              { ring:'Patch Tuesday', color:'#1a56db', pct:'0%',  icon:'🗓️', desc:'Microsoft releases patches' },
              { ring:'Test Ring',     color:'#1565c0', pct:'5%',  icon:'🧪', desc:'Phase 1: IT lab & test devices (Sun +5 days)' },
              { ring:'Pilot Ring',    color:'#b45309', pct:'20%', icon:'🚀', desc:'Phase 2: Pilot group (Sun +12 days)' },
              { ring:'Production',    color:'#7c3aed', pct:'100%',icon:'✅', desc:'Phase 3+4: Full production rollout' },
            ].map((c,i) => (
              <div key={i} style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px',
                borderLeft:`4px solid ${c.color}` }}>
                <div style={{ fontSize:20, marginBottom:6 }}>{c.icon}</div>
                <div style={{ fontSize:13, fontWeight:700, color:c.color }}>{c.ring}</div>
                <div style={{ fontSize:11, color:'#64748b', marginTop:4 }}>{c.desc}</div>
                <div style={{ background:'#e8ecf4', borderRadius:4, height:4, marginTop:10, overflow:'hidden' }}>
                  <div style={{ width:c.pct, height:'100%', background:c.color, borderRadius:4 }} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 18px', marginTop:16,
            fontSize:12, color:'#64748b', lineHeight:1.7 }}>
            <strong style={{ color:'#1e2a3b' }}>📌 Notes:</strong>
            <ul style={{ margin:'6px 0 0', paddingLeft:20 }}>
              <li>Patch Tuesday = 2nd Tuesday of each month (Microsoft security release day)</li>
              <li>Test ring receives patches first (Phase 1, Sunday ~5 days after Patch Tuesday)</li>
              <li>Pilot ring receives patches in Phase 2 (~12 days after Patch Tuesday)</li>
              <li>Production receives patches in Phase 3 (~18 days) and Phase 4 (~25 days)</li>
              <li>Each phase gap allows time for rollback if issues are detected in earlier rings</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
